import React, { useMemo } from 'react';
import { findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { Box, Button, CardActions, CardContent, Divider, Grid, Paper, Typography } from '@availity/element';

import { AvailityHeaderLogo, Footer, TwoFAMethodTable } from '@/components';
import { useFRCallback } from '@/hooks';

const TwoFAManageHub = ({ ...props }) => {
  const { stepToRender, setCurrentStep } = props;
  const {
    callbacks = [],
    choiceCallbackIndex,
    choiceCallbackChoices,
    confirmationCallbackIndex,
    confirmationCallbackOptions,
  } = useFRCallback(stepToRender);

  // get options that render in radio group
  const choiceOptionsParsed = useMemo(() => {
    const output = [];

    if (choiceCallbackChoices) {
      for (const [index, choice] of choiceCallbackChoices.entries()) {
        const parsedChoice = JSON.parse(choice);
        parsedChoice.methodIndex = index;
        output.push(parsedChoice);
      }
    }

    return output;
  }, [choiceCallbackChoices]);

  // get index of the remove option in the confirmation callback
  const removeButtonIndex = useMemo(() => {
    const removeIndex = findIndex(confirmationCallbackOptions, (option) => option?.toLowerCase() === 'remove');

    return removeIndex;
  }, [confirmationCallbackOptions]);

  // handle method removal
  const handleRemoveMethod = (methodIndex) => {
    const nextStep = { ...stepToRender };
    const newCallbacks = [...callbacks];

    // set the choice callback input to the methodIndex
    if (methodIndex) {
      newCallbacks[choiceCallbackIndex].input[0] = {
        ...newCallbacks[choiceCallbackIndex].input[0],
        value: methodIndex,
      };
    }

    // set the confirmation callback input value to the removeButtonIndex
    newCallbacks[confirmationCallbackIndex].input[0] = {
      ...newCallbacks[confirmationCallbackIndex].input[0],
      value: removeButtonIndex,
    };

    // set the new callbacks in the next step
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // update the currentStep to trigger update
    setCurrentStep({ ...nextStep });
  };

  // handle other navigation
  const handleNavigation = (selectedNavigation) => {
    const formattedSelectedNavigation = selectedNavigation?.toLowerCase();
    const nextStep = { ...stepToRender };

    if (formattedSelectedNavigation) {
      const newCallbacks = [...callbacks];

      const navigationIndex = findIndex(
        confirmationCallbackOptions,
        (option) => option?.toLowerCase() === formattedSelectedNavigation
      );

      if (navigationIndex >= 0) {
        // set the confirmation callback input value to the removeButtonIndex
        newCallbacks[confirmationCallbackIndex].input[0] = {
          ...newCallbacks[confirmationCallbackIndex].input[0],
          value: navigationIndex,
        };
      }

      // set the new callbacks in the next step
      Object.assign(nextStep.payload.callbacks, newCallbacks);
    }

    // update the currentStep to trigger update
    setCurrentStep({ ...nextStep });
  };

  return (
    <Grid
      id="2fa-manage-hub-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px', md: '600px' } }}>
            <CardContent>
              <Typography variant="h1" mb={1}>
                Security Settings
              </Typography>
              <Typography variant="h2" mb={2}>
                2-Step Authentication
              </Typography>
              <Divider sx={{ marginY: 2 }} />
              <TwoFAMethodTable methodArray={choiceOptionsParsed} removeMethod={handleRemoveMethod} />
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between' }}>
              <Box>
                <Button color="secondary" onClick={() => handleNavigation('back')}>
                  Back
                </Button>
              </Box>
              <Box>
                <Button color="secondary" onClick={() => handleNavigation('showcodes')}>
                  Show backup codes
                </Button>{' '}
                <Button color="primary" onClick={() => handleNavigation('enroll')}>
                  Add another device
                </Button>
              </Box>
            </CardActions>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

TwoFAManageHub.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default TwoFAManageHub;
